import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import {IconComponent} from '../../../../shared/icons/icon.component';
import {ContextMenuComponent, ContextMenuData} from '../../../../shared/components/context-menu/context-menu.component';
import {IconsPipe} from '../../../../shared/pipes/icons.pipe';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {RouteConstants} from '../../../../shared/constants/route-constants';
import {ConfirmDialogComponent, ConfirmDialogData} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {filter} from 'rxjs';
import {MatDialog} from '@angular/material/dialog';
import {RelativeDatePipe} from '../../../../shared/pipes/relative-date.pipe';
import {PowerItemComponent} from '../power-item/power-item.component';
import {DeviceResponseDto} from '../../models/device-response.interface';
import {isDeviceActive, isDevicePending} from '../../utils/device-value.utils';
import {LocalizedNumberPipe} from '../../../../shared/pipes/localized-number.pipe';
import {TagComponent} from '../../../../shared/components/tag/tag.component';
import { LabelConverter } from '../../../../shared/converter/label.converter';
import { DeviceActionTriggerComponent } from "../device-action-trigger/device-action-trigger.component";
import { getDeviceDetailRoute } from '../../../../shared/utils/device-route.util';

@Component({
  selector: 'tr[app-device-item-row]',
  imports: [
    IconComponent,
    ContextMenuComponent,
    IconsPipe,
    TranslatePipe,
    LocalizedNumberPipe,
    RelativeDatePipe,
    PowerItemComponent,
    TagComponent,
    DeviceActionTriggerComponent
],
  templateUrl: './device-item-row.component.html',
  styleUrl: './device-item-row.component.scss'
})
export class DeviceItemRowComponent {
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private translateService = inject(TranslateService);

  @Input() isHeader = false;
  @Input() device?: DeviceResponseDto;
  @Input() disabled: boolean = false;
  @Input() hideActions: boolean = false;
  @Output() triggerDeviceAction = new EventEmitter<{ id: string, value: boolean }>()
  @Output() delete = new EventEmitter<string>()
  @Output() edit = new EventEmitter<string>()

  private readonly fullContextMenu: ContextMenuData[] = [
    {
      labelKey: "default.details",
      action: () => this.router.navigate(this.device ? getDeviceDetailRoute(this.device) : [RouteConstants.routeDevice, this.device])
    },
    {
      labelKey: "default.edit",
      action: () => this.edit.emit(this.device?.id)
    },
    {
      labelKey: "default.delete",
      action: () => this.onDelete()
    }
  ];

  get contextMenu(): ContextMenuData[] {
    if (this.hideActions) {
      return [this.fullContextMenu[0]];
    }

    return this.fullContextMenu;
  }

  onTriggerDeviceAction(value: boolean) {
    if (!this.device?.isActuator || this.isInteractionDisabled) return
    this.triggerDeviceAction.emit({
      id: this.device.id,
      value: value,
    })
  }

  get isActive() {
    return !!this.device?.isActuator && isDeviceActive(this.device)
  }

  get isPending(): boolean {
    return !!this.device?.isActuator && isDevicePending(this.device);
  }

  get isInteractionDisabled() {
    return this.disabled || this.device?.isOutOfService === true;
  }

  onDelete(): void {
    const dialogData: ConfirmDialogData = {
      title: this.translateService.instant('devices.delete.title'),
      body: this.translateService.instant('devices.delete.body'),
      confirmLabel: this.translateService.instant('default.delete'),
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed()
      .pipe(
        filter((result): result is true => result === true)
      )
      .subscribe(_ => {
        this.delete.emit(this.device?.id)
      });
  }

  protected readonly LabelConverter = LabelConverter;
  protected readonly isDevicePending = isDevicePending;
}
